// src/components/sections/ProjectProgress.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  LinearProgress,
  Button,
  Chip
} from '@mui/material';
import { CURRENT_DATE } from '../../utils/constants'; // Import the constant
import { Logger } from '../../utils/logger';

const logger = new Logger('ProjectProgress');

/**
 * ProjectProgress component displays the project progress information
 * including time elapsed, hours billed, and budget spent.
 */
const ProjectProgress = ({
  projectId,
  progressData,
  totalBudget: totalBudgetProp,
  startDate,
  endDate
}) => {
  // Use the passed totalBudget (consistent with header) or fall back to progressData
  // Use Number() coercion to guard against Decimal128/string values from MongoDB
  const effectiveTotalBudget = Number(totalBudgetProp) || Number(progressData.total_budget) || 0;
  // Track which progress metric is currently displayed
  const [activeView, setActiveView] = useState('time');

  // Log progressData when it changes to help debug
  React.useEffect(() => {
    logger.debug(`Project ${projectId} progress data:`, {
      hours_billed: progressData.hours_billed,
      hours_allocated: progressData.hours_allocated,
      hours_billed_percentage: progressData.hours_billed_percentage,
      calculated_percentage: progressData.hours_allocated > 0
        ? ((progressData.hours_billed / progressData.hours_allocated) * 100).toFixed(2) + '%'
        : 'N/A'
    });
  }, [projectId, progressData]);
  
  // Calculate time in weeks using the CURRENT_DATE constant
  const calculateTimeInWeeks = () => {
    if (!startDate || !endDate) {
      return { totalWeeks: 0, elapsedWeeks: 0 };
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = CURRENT_DATE; // Use the constant instead of new Date()
    
    // Calculate total project duration in milliseconds
    const totalDuration = end.getTime() - start.getTime();
    // Calculate elapsed time in milliseconds
    const elapsedDuration = today.getTime() > start.getTime() 
      ? Math.min(today.getTime() - start.getTime(), totalDuration) 
      : 0;
    
    // Convert to weeks (1 week = 7 days = 604800000 ms)
    const totalWeeks = Math.ceil(totalDuration / (7 * 24 * 60 * 60 * 1000));
    const elapsedWeeks = Math.ceil(elapsedDuration / (7 * 24 * 60 * 60 * 1000));
    
    return { totalWeeks, elapsedWeeks };
  };
  
  // Format currency with $ sign and commas, rounded to nearest dollar
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0';
    
    const numValue = typeof amount === 'string' ? 
      parseFloat(amount.replace(/[$,]/g, '')) : 
      Number(amount);
    
    if (isNaN(numValue)) return '$0';
    
    // Round to nearest dollar and format with commas
    return `$${Math.round(numValue).toLocaleString('en-US')}`;
  };
  
  // Get the current progress percentage based on active view
  const getProgressPercentage = () => {
    switch (activeView) {
      case 'time': {
        const pct = progressData.time_elapsed_percentage !== null ? progressData.time_elapsed_percentage || 0 : 0;
        return Math.min(pct, 100);
      }
      case 'hours': {
        // Calculate from raw hours values instead of using hours_billed_percentage
        if (progressData.hours_billed !== undefined && progressData.hours_billed !== null &&
            progressData.hours_allocated !== undefined && progressData.hours_allocated !== null &&
            progressData.hours_allocated > 0) {
          const percentage = (progressData.hours_billed / progressData.hours_allocated) * 100;
          // Cap at 100% for the visual bar, even if over-billed
          return Math.min(percentage, 100);
        }
        return 0;
      }
      case 'budget': {
        if (progressData.budget_spent === null || effectiveTotalBudget === null) return 0;
        if (!progressData.budget_spent || !effectiveTotalBudget) return 0;
        // For budget, always fill 100% if over budget, otherwise show actual percentage
        const budgetPercentage = (progressData.budget_spent / effectiveTotalBudget) * 100;
        return budgetPercentage > 100 ? 100 : budgetPercentage;
      }
      default:
        return 0;
    }
  };
  
  // Get the display percentage (can exceed 100% for all views — projects run over)
  const getDisplayPercentage = () => {
    // Time elapsed — use backend value (correctly handles completed vs in-progress,
    // and uses last-timesheet date for completed projects instead of CURRENT_DATE)
    if (activeView === 'time') {
      return progressData.time_elapsed_percentage != null
        ? Math.round(progressData.time_elapsed_percentage)
        : 0;
    }
    
    // Hours billed view - calculate based on hours
    if (activeView === 'hours' && 
        progressData.hours_billed !== undefined && progressData.hours_billed !== null &&
        progressData.hours_allocated !== undefined && progressData.hours_allocated !== null &&
        progressData.hours_allocated > 0) {
      // Allow percentage to exceed 100%
      return Math.round((progressData.hours_billed / progressData.hours_allocated) * 100);
    }
    
    // Budget view - keep existing calculation
    if (activeView === 'budget' && 
        progressData.budget_spent !== null && effectiveTotalBudget !== null && 
        progressData.budget_spent && effectiveTotalBudget) {
      // Calculate actual percentage for budget, potentially over 100%
      return Math.round((progressData.budget_spent / effectiveTotalBudget) * 100);
    }
    
    // Fallback to capped percentage — guard against NaN from bad numeric data
    const result = Math.round(getProgressPercentage());
    return isNaN(result) ? 0 : result;
  };
  
  // Determine color based on progress status and active view
  const getProgressColor = () => {
    // Get the actual percentage (potentially over 100%) based on active view
    let actualPercentage = 0;
    
    if (activeView === 'budget') {
      // Budget percentage calculation
      actualPercentage = progressData.budget_spent && effectiveTotalBudget ?
        (progressData.budget_spent / effectiveTotalBudget) * 100 : 0;
    } 
    else if (activeView === 'time') {
      // Use backend value — correctly handles completed vs in-progress projects
      actualPercentage = progressData.time_elapsed_percentage || 0;
    }
    else if (activeView === 'hours') {
      // Hours billed percentage calculation
      actualPercentage = progressData.hours_billed !== undefined && 
                         progressData.hours_billed !== null &&
                         progressData.hours_allocated !== undefined && 
                         progressData.hours_allocated !== null &&
                         progressData.hours_allocated > 0 ?
        (progressData.hours_billed / progressData.hours_allocated) * 100 : 0;
    }
    
    // Apply color based on percentage
    if (actualPercentage > 100) {
      return '#CF6679'; // Red for over 100%
    }
    if (actualPercentage > 85) {
      return '#FFCF44'; // Yellow for approaching 100%
    }
    
    return '#03DAC6'; // Default teal for normal
  };
  
  // Get the display text based on active view
  const getProgressText = () => {
    // Get the actual percentage (can exceed 100%)
    const displayPercentage = getDisplayPercentage();
    
    switch (activeView) {
      case 'time': {
        if (progressData.time_elapsed_percentage === null) {
          return 'No time elapsed data available';
        }
        
        const { totalWeeks, elapsedWeeks } = calculateTimeInWeeks();
        
        if (totalWeeks > 0) {
          return `${elapsedWeeks} of ${totalWeeks} weeks elapsed (${displayPercentage}%)`;
        }
        return `${displayPercentage}% of time elapsed`;
      }
      case 'hours': {
        if (progressData.hours_billed === null || progressData.hours_allocated === null) {
          return 'No hours billed data available';
        }
        
        // Make sure we're using the actual values, not undefined or null
        const hoursBilled = progressData.hours_billed !== undefined && progressData.hours_billed !== null 
          ? progressData.hours_billed 
          : 0;
        const hoursAllocated = progressData.hours_allocated !== undefined && progressData.hours_allocated !== null 
          ? progressData.hours_allocated 
          : 0;
        
        return `${hoursBilled} of ${hoursAllocated} estimated hours billed (${displayPercentage}%)`;
      }
      case 'budget':
        if (progressData.budget_spent === null || effectiveTotalBudget === null) {
          return 'No budget data available';
        }
        return formatBudgetSpent();
      default:
        return '';
    }
  };
  
  // Format budget spent information
  const formatBudgetSpent = () => {
    if (progressData.budget_spent === null || effectiveTotalBudget === null) {
      return 'No budget data available';
    }
    
    const spent = formatCurrency(progressData.budget_spent || 0);
    const total = formatCurrency(effectiveTotalBudget || 0);
    const displayPercentage = getDisplayPercentage();
    
    return `${spent} of ${total} budgeted spend (${displayPercentage}%)`;
  };
  
  // Determine if we're over budget
  const isOverBudget = () => {
    if (progressData.budget_spent === null || effectiveTotalBudget === null) return false;
    if (!progressData.budget_spent || !effectiveTotalBudget) return false;
    return progressData.budget_spent > effectiveTotalBudget;
  };
  
  return (
    <Box sx={{ px: 1.5, pt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
          PROJECT PROGRESS
        </Typography>
        <Chip 
          label={`${getDisplayPercentage()}%`}
          size="small"
          sx={{ 
            bgcolor: `${getProgressColor()}30`,
            color: getProgressColor(),
            fontWeight: 'bold',
            height: 20,
            fontSize: '0.65rem'
          }}
        />
      </Box>

      <LinearProgress 
        variant="determinate" 
        value={getProgressPercentage()} 
        sx={{ 
          height: 8,
          borderRadius: 5,
          bgcolor: '#111',
          mb: 1,
          '& .MuiLinearProgress-bar': {
            borderRadius: activeView === 'budget' && isOverBudget() ? '5px 0 0 5px' : 5,
            bgcolor: getProgressColor()
          }
        }}
      />

      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Button
          variant={activeView === 'time' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setActiveView('time')}
          sx={{ 
            fontSize: '0.65rem', 
            borderRadius: 1,
            color: activeView === 'time' ? '#fff' : 'text.secondary',
            borderColor: 'rgba(255,255,255,0.15)',
            bgcolor: activeView === 'time' ? 'rgba(125,125,125,0.2)' : 'rgba(255,255,255,0.05)',
            flex: 1,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: activeView === 'time' ? 'rgba(150,150,150,0.3)' : 'rgba(255,255,255,0.1)',
              borderColor: 'rgba(255,255,255,0.2)',
              boxShadow: 'none',
            }
          }}
        >
          TIME ELAPSED
        </Button>
        <Button
          variant={activeView === 'hours' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setActiveView('hours')}
          sx={{ 
            fontSize: '0.65rem',
            borderRadius: 1,
            color: activeView === 'hours' ? '#fff' : 'text.secondary',
            borderColor: 'rgba(255,255,255,0.15)',
            bgcolor: activeView === 'hours' ? 'rgba(125,125,125,0.2)' : 'rgba(255,255,255,0.05)',
            flex: 1,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: activeView === 'hours' ? 'rgba(150,150,150,0.3)' : 'rgba(255,255,255,0.1)',
              borderColor: 'rgba(255,255,255,0.2)',
              boxShadow: 'none',
            }
          }}
        >
          HOURS BILLED
        </Button>
        <Button
          variant={activeView === 'budget' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setActiveView('budget')}
          sx={{ 
            fontSize: '0.65rem',
            borderRadius: 1,
            color: activeView === 'budget' ? '#fff' : 'text.secondary',
            borderColor: 'rgba(255,255,255,0.15)',
            bgcolor: activeView === 'budget' ? 'rgba(125,125,125,0.2)' : 'rgba(255,255,255,0.05)',
            flex: 1,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: activeView === 'budget' ? 'rgba(150,150,150,0.3)' : 'rgba(255,255,255,0.1)',
              borderColor: 'rgba(255,255,255,0.2)',
              boxShadow: 'none',
            }
          }}
        >
          BUDGET SPENT
        </Button>
      </Box>

      <Typography variant="body2" sx={{ fontSize: '0.75rem', textAlign: 'center', mb: 1 }}>
        {getProgressText()}
      </Typography>
    </Box>
  );
};

ProjectProgress.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  progressData: PropTypes.shape({
    time_elapsed_percentage: PropTypes.number,
    hours_billed_percentage: PropTypes.number,
    budget_spent_percentage: PropTypes.number,
    budget_spent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_budget: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    hours_billed: PropTypes.number,
    hours_allocated: PropTypes.number
  }),
  totalBudget: PropTypes.number,
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
};

export default ProjectProgress;
